/**
 * Renders a form from an array of Oracle BI Publisher parameters.
 * - Date types → HTML5 date picker
 * - Menu types → dropdown (single or multi)
 * - String/other → text input
 *
 * Three action buttons send format to the backend: 'pdf', 'xlsx', 'json'.
 */
function ParamField({ param, value, onChange }) {
  const {
    name,
    label,
    UIType,
    defaultValue,
    values = [],
    lovLabels = [],
    multiValuesAllowed,
  } = param;
  const displayLabel = label || name;
  const hasLabels = Array.isArray(lovLabels) && lovLabels.length > 0;
  const optionValues = Array.isArray(values) && values.length > 0 ? values : [];
  const options = hasLabels
    ? lovLabels.map((l, i) => ({ label: l, value: optionValues[i] ?? l }))
    : optionValues.map((v) => ({ label: v, value: v }));
  if (options.length === 0 && hasLabels) {
    lovLabels.forEach((l) => options.push({ label: l, value: l }));
  }

  const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500';

  if (UIType === 'Hidden') return null;

  // Date → HTML5 date picker
  if (UIType === 'Date') {
    return (
      <div className="mb-4">
        <label htmlFor={`param-${name}`} className="block text-sm font-medium text-gray-700">
          {displayLabel}
        </label>
        <input
          id={`param-${name}`}
          type="date"
          value={value ?? defaultValue ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      </div>
    );
  }

  // Menu → dropdown
  if (UIType === 'Menu' && options.length > 0) {
    return (
      <div className="mb-4">
        <label htmlFor={`param-${name}`} className="block text-sm font-medium text-gray-700">
          {displayLabel}
        </label>
        <select
          id={`param-${name}`}
          value={Array.isArray(value) ? value[0] : value ?? defaultValue ?? ''}
          onChange={(e) =>
            onChange(
              multiValuesAllowed
                ? Array.from(e.target.selectedOptions, (o) => o.value)
                : e.target.value
            )
          }
          multiple={multiValuesAllowed}
          className={inputClass}
        >
          <option value="">-- Select --</option>
          {options.map((opt, i) => (
            <option key={i} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // String / Text / Search / default → text input
  return (
    <div className="mb-4">
      <label htmlFor={`param-${name}`} className="block text-sm font-medium text-gray-700">
        {displayLabel}
      </label>
      <input
        id={`param-${name}`}
        type="text"
        value={value ?? defaultValue ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  );
}

export default function ParameterForm({
  parameters = [],
  value = {},
  onChange,
  onRunReport,
  loading = false,
  buttonClassName = '',
  selectedFormat = 'pdf',
  onFormatChange,
}) {
  const normalizedParams = parameters.map((p) => p.listOfParamNameValues?.[0] || p).filter(Boolean);

  const handleRunClick = () => {
    if (onRunReport && !loading) {
      onRunReport(selectedFormat);
    }
  };

  return (
    <div className="parameter-form">
      {normalizedParams.length === 0 ? (
        <p className="text-sm text-gray-500">No parameters for this report.</p>
      ) : (
        <div className="space-y-0">
          {normalizedParams.map((param) => (
            <ParamField
              key={param.name}
              param={param}
              value={value[param.name]}
              onChange={(v) => onChange?.({ ...value, [param.name]: v })}
            />
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4">
        <label htmlFor="report-format" className="text-sm font-medium text-gray-700">
          Format
        </label>
        <select
          id="report-format"
          value={selectedFormat}
          onChange={(e) => onFormatChange?.(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        >
          <option value="pdf">PDF</option>
          <option value="xlsx">XLSX</option>
          <option value="csv">CSV</option>
          <option value="json">JSON (Preview)</option>
        </select>
        <button
          type="button"
          onClick={handleRunClick}
          disabled={loading}
          className={
            buttonClassName ||
            'inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-70'
          }
        >
          {loading ? 'Running…' : 'Run'}
        </button>
      </div>
    </div>
  );
}
